// src/components/forms/helpers/kennelHelpers.ts

import type { CustomIconName } from '../../../utils/customIcons';

export interface Kennel {
  id: number;
  kennelNumber: string;
  size: string;
  kennelSideId: number;
}

export interface KennelSelection {
  id: number;
  kennelSideOptionId: number | null;
  kennelSideId: number | null;
  size: string | null;
  kennelId: number | null;
  heaterEnabled?: boolean;
  heaterNights?: number;
  unavailableKennelLabel?: string;
}

/**
 * Extract kennel side prefix from display name (A, B, or C)
 * @param displayName - The kennel side option display name
 * @returns 'A', 'B', 'C', or empty string
 */
export const getKennelSidePrefix = (displayName: string): string => {
  if (displayName.startsWith('A')) return 'A';
  if (displayName.startsWith('B')) return 'B';
  if (displayName.includes('Cattery')) return 'C';
  return '';
};

/**
 * Get all kennels matching a specific side and size
 * @param kennelSideId - The kennel side ID to filter by
 * @param size - The kennel size to filter by (null = any size)
 * @param availableKennels - List of available kennels
 * @returns Filtered list of kennels
 */
export const getKennelsForSelection = (
  kennelSideId: number | null,
  size: string | null,
  availableKennels: Kennel[]
): Kennel[] => {
  if (!kennelSideId) return [];

  return availableKennels.filter(
    (k) => k.kennelSideId === kennelSideId && (size === null || k.size === size)
  );
};

/**
 * Get set of all currently selected kennel IDs
 * @param kennelSelections - Current kennel selections
 * @param excludeSelectionId - Optional selection ID to exclude from result
 * @returns Set of selected kennel IDs
 */
export const getSelectedKennelIds = (
  kennelSelections: KennelSelection[],
  excludeSelectionId?: number
): Set<number> => {
  return new Set(
    kennelSelections
      .filter((sel) => sel.kennelId !== null && sel.id !== excludeSelectionId)
      .map((sel) => sel.kennelId!)
  );
};

/**
 * Check if there are unselected kennels available for a side/size
 * @param kennelSideId - The kennel side ID
 * @param size - The kennel size (null = any size)
 * @param kennelSelections - Current selections
 * @param availableKennels - Available kennels
 * @param excludeSelectionId - Optional selection ID to exclude
 * @returns True if at least one unselected kennel is available
 */
export const hasAvailableUnselectedKennels = (
  kennelSideId: number,
  size: string | null,
  kennelSelections: KennelSelection[],
  availableKennels: Kennel[],
  excludeSelectionId?: number
): boolean => {
  const availableForSide = getKennelsForSelection(
    kennelSideId,
    size,
    availableKennels
  );
  const selectedIds = getSelectedKennelIds(
    kennelSelections,
    excludeSelectionId
  );

  return availableForSide.some((kennel) => !selectedIds.has(kennel.id));
};

/**
 * Get the first available unselected kennel for a side/size
 * @param kennelSideId - The kennel side ID
 * @param size - The kennel size (null = any size)
 * @param kennelSelections - Current selections
 * @param availableKennels - Available kennels
 * @param excludeSelectionId - Optional selection ID to exclude
 * @returns First available unselected kennel or null
 */
export const getFirstAvailableUnselectedKennel = (
  kennelSideId: number | null,
  size: string | null,
  kennelSelections: KennelSelection[],
  availableKennels: Kennel[],
  excludeSelectionId?: number
): Kennel | null => {
  const availableForSide = getKennelsForSelection(
    kennelSideId,
    size,
    availableKennels
  );
  const selectedIds = getSelectedKennelIds(
    kennelSelections,
    excludeSelectionId
  );

  return availableForSide.find((kennel) => !selectedIds.has(kennel.id)) || null;
};

/**
 * Clear any selected kennel that is missing from the fresh availability list
 * and flag the row with the cleared kennel's label.
 * @param selections - Current selections
 * @param availableKennels - Fresh availability for the current dates
 * @param getLabel - Builds the display label for a cleared selection
 * @returns The same array when nothing changed, otherwise an updated copy
 */
export const clearUnavailableKennels = (
  selections: KennelSelection[],
  availableKennels: Kennel[],
  getLabel: (selection: KennelSelection) => string
): KennelSelection[] => {
  let changed = false;
  const result = selections.map((sel) => {
    if (
      sel.kennelId === null ||
      availableKennels.some((k) => k.id === sel.kennelId)
    ) {
      return sel;
    }
    changed = true;
    return { ...sel, kennelId: null, unavailableKennelLabel: getLabel(sel) };
  });
  return changed ? result : selections;
};

/**
 * Check if any selection is flagged as unavailable
 * @param selections - Current selections
 * @returns True if at least one row needs the user to choose a kennel again
 */
export const hasUnavailableKennels = (selections: KennelSelection[]): boolean =>
  selections.some((sel) => sel.unavailableKennelLabel !== undefined);

/**
 * Check whether a kennel side houses at least one of the booking's pet types.
 * No restriction is applied when either list is missing or empty.
 * @param allowedPetTypeIds - Pet type ids the side accepts
 * @param petTypeIds - Pet type ids of the pets on the booking
 * @returns True if the side may be offered for these pets
 */
export const sideAcceptsPetTypes = (
  allowedPetTypeIds: number[] | undefined,
  petTypeIds: number[]
): boolean => {
  if (!allowedPetTypeIds || petTypeIds.length === 0) return true;
  return petTypeIds.some((id) => allowedPetTypeIds.includes(id));
};

/**
 * Check whether a selection row has actually chosen a room: a specific kennel,
 * or a communal side with no individual kennels.
 * @param selection - The kennel selection
 * @returns True if the row counts towards the booking's rooms
 */
export const isRoomSelected = (selection: KennelSelection): boolean =>
  selection.kennelId != null ||
  (selection.kennelSideId !== null && selection.size === null);

/**
 * Count the rows that have chosen a room
 * @param selections - Current selections
 * @returns Number of kennels and communal rooms chosen
 */
export const countSelectedRooms = (selections: KennelSelection[]): number =>
  selections.filter(isRoomSelected).length;

export interface PlacementSideOption {
  kennelSideId: number;
  displayName: string;
  allowedPetTypeIds?: number[];
}

export interface PlacementItem {
  label: string;
  /** Pet type whose icon stands for this room: the lowest id the room accepts. */
  petTypeId: number;
}

export type PlacementProblem =
  | { kind: 'missing'; items: PlacementItem[] }
  | { kind: 'unsuitable'; text: string };

// Seeded pet types are IsSystem-locked, so their ids and names are fixed.
const PET_TYPE_ICONS: Record<number, CustomIconName> = {
  1: 'dog',
  2: 'cat',
  3: 'bird',
  4: 'rodent',
  5: 'reptile',
  6: 'fish',
};

/**
 * Icon name for a pet type id, or undefined for a type with no icon.
 * @param petTypeId - The pet type id
 * @returns The custom icon name
 */
export const getPetTypeIcon = (petTypeId: number): CustomIconName | undefined =>
  PET_TYPE_ICONS[petTypeId];

const optionName = (displayName: string): string =>
  displayName.replace('Cattery Standard', 'Cattery');

/**
 * Short label for a kennel side, taken from the words its options share
 * (Cattery, Cattery Communal and Cattery Double give "Cattery"). A side whose
 * options share only a letter or nothing (A Double, A Triple) is just a
 * kennel, as is a lone option that starts with a single letter.
 */
const sideLabel = (displayNames: string[]): string => {
  const names = Array.from(new Set(displayNames.map(optionName)));
  if (names.length === 1) {
    return names[0].split(' ')[0].length > 1 ? names[0] : 'Kennel';
  }
  const words = names.map((n) => n.split(' '));
  const shared: string[] = [];
  for (let i = 0; i < words[0].length; i++) {
    if (words.every((w) => w[i] === words[0][i])) shared.push(words[0][i]);
    else break;
  }
  const label = shared.join(' ');
  return label.length > 2 ? label : 'Kennel';
};

/**
 * Mirror of the server's placement rule for the rooms chosen so far: every pet
 * type needs a chosen side that accepts it, and no chosen side may accept none
 * of the booking's pet types.
 * @param selections - Current selections
 * @param sideOptions - Kennel side options with their accepted pet types
 * @param petTypes - Distinct pet types of the selected pets
 * @returns The problem (missing room or unsuitable room), or null when placement is fine
 */
export const getPlacementProblem = (
  selections: KennelSelection[],
  sideOptions: PlacementSideOption[],
  petTypes: { id: number; name: string }[]
): PlacementProblem | null => {
  const sideIds = Array.from(
    new Set(
      selections
        .filter(isRoomSelected)
        .map((sel) => sel.kennelSideId)
        .filter((id): id is number => id !== null)
    )
  );
  if (petTypes.length === 0 || sideIds.length === 0) return null;

  const allowedFor = (sideId: number) =>
    sideOptions.find((o) => o.kennelSideId === sideId)?.allowedPetTypeIds;
  const petTypeIds = petTypes.map((pt) => pt.id);

  const uncovered = petTypes.filter((pt) =>
    sideIds.every((sideId) => !sideAcceptsPetTypes(allowedFor(sideId), [pt.id]))
  );
  if (uncovered.length > 0) {
    const labels = new Map<string, number>();
    const addLabel = (label: string, petTypeId: number) =>
      labels.set(label, Math.min(labels.get(label) ?? petTypeId, petTypeId));
    uncovered.forEach((pt) => {
      const accepting = new Map<
        number,
        { names: string[]; petTypeIds: number[] }
      >();
      sideOptions
        .filter((o) => sideAcceptsPetTypes(o.allowedPetTypeIds, [pt.id]))
        .forEach((o) => {
          const entry = accepting.get(o.kennelSideId) ?? {
            names: [],
            petTypeIds: [],
          };
          entry.names.push(o.displayName);
          entry.petTypeIds.push(...(o.allowedPetTypeIds ?? []));
          accepting.set(o.kennelSideId, entry);
        });
      if (accepting.size === 0) {
        addLabel(`Room for ${pt.name}`, pt.id);
      } else {
        accepting.forEach(({ names, petTypeIds }) =>
          addLabel(
            sideLabel(names),
            petTypeIds.length > 0 ? Math.min(...petTypeIds) : pt.id
          )
        );
      }
    });
    const items = Array.from(labels, ([label, petTypeId]) => ({
      label,
      petTypeId,
    })).sort((a, b) => a.label.localeCompare(b.label));
    return { kind: 'missing', items };
  }

  const stray = sideIds.filter(
    (sideId) => !sideAcceptsPetTypes(allowedFor(sideId), petTypeIds)
  );
  if (stray.length > 0) {
    const names = stray.map(
      (sideId) =>
        sideOptions.find((o) => o.kennelSideId === sideId)?.displayName ||
        'A selected kennel'
    );
    return {
      kind: 'unsuitable',
      text: `${names.join(', ')} ${
        names.length > 1 ? 'do' : 'does'
      } not accept the pet types in this booking (${petTypes
        .map((pt) => pt.name)
        .join(', ')}).`,
    };
  }

  return null;
};

/**
 * Check if a kennel option has actual kennels to select (vs communal/animal room)
 * @param selection - The kennel selection
 * @returns True if size is not null (has individual kennels)
 */
export const selectedOptionHasKennels = (
  selection: KennelSelection
): boolean => {
  return selection.size !== null;
};

/**
 * Check if a kennel side option supports heater configuration
 * @param displayName - The kennel side option display name
 * @returns True if heater is supported
 */
export const supportsHeater = (
  displayName: string | null | undefined
): boolean => {
  if (!displayName) return false;
  return (
    !displayName.includes('Cattery Communal') &&
    !displayName.includes('animal room')
  );
};
